import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { regionMarkerId } from './weatherConstants';
import { shipmentPinLatLng } from '../tracking/portCoords';
import { voyageHasDelay } from '../tracking/ShipMarker';
import { pickMapHit } from '../../../utils/mapHit';
import { shipmentHeading } from '../../../utils/shipHeading';

export function MapResizeHandler() {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    if (!container) return;

    const initialTimer = setTimeout(() => {
      map.invalidateSize({ animate: false });
    }, 250);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
        map.invalidateSize({ animate: false });
      }
    });

    resizeObserver.observe(container);

    const handleWindowResize = () => {
      map.invalidateSize({ animate: false });
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      clearTimeout(initialTimer);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [map]);

  return null;
}

/**
 * Disables all programmatic zoom handlers on the Leaflet map instance.
 * This catches trackpad pinch-to-zoom which fires as wheel events with ctrlKey=true,
 * bypassing the scrollWheelZoom={false} prop on MapContainer.
 */
export function DisableZoomHandlers() {
  const map = useMap();

  useEffect(() => {
    map.scrollWheelZoom.disable();
    map.touchZoom.disable();
    map.doubleClickZoom.disable();
    map.boxZoom.disable();

    // Intercept wheel events on the container to block ctrlKey+wheel (trackpad pinch)
    const container = map.getContainer();
    const blockWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    container.addEventListener('wheel', blockWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', blockWheel);
    };
  }, [map]);

  return null;
}

export function ShipMarkerPanes() {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    if (!map.getPane('shipNormal')) {
      const pane = map.createPane('shipNormal');
      pane.style.zIndex = 620;
    }
    if (!map.getPane('shipAhead')) {
      const pane = map.createPane('shipAhead');
      pane.style.zIndex = 650;
    }
    if (!map.getPane('shipDelayed')) {
      const pane = map.createPane('shipDelayed');
      pane.style.zIndex = 690; // keep below Leaflet's popupPane (700) so popups cover delayed ships
    }
  }, [map]);
  return null;
}

export function MapClickHandler({ onMapClick }) {
  const map = useMap();

  useEffect(() => {
    const handler = () => onMapClick();
    map.on('click', handler);
    return () => map.off('click', handler);
  }, [map, onMapClick]);

  return null;
}

// Ship icon is a 43px box (24-unit viewBox) with the arrow drawn in its upper
// half, so the visible triangle's centre is ~8.5px ahead of the anchor.
const ARROW_CENTER_OFFSET_PX = 8.5;

/** Pixel hit-test on the map container (capture). Nearest arrow in radius wins. */
export function ShipClickResolver({ rows, onHit, onMiss, onHoverChange, modeRows, onModeHoverChange, onModeHit }) {
  const map = useMap();
  const onHitRef = useRef(onHit);
  const onMissRef = useRef(onMiss);
  const onHoverRef = useRef(onHoverChange);
  onHitRef.current = onHit;
  onMissRef.current = onMiss;
  onHoverRef.current = onHoverChange;
  // Air/truck markers: a second candidate set used for hover only. Sea wins.
  const modeRowsRef = useRef(modeRows);
  const onModeHoverRef = useRef(onModeHoverChange);
  modeRowsRef.current = modeRows;
  const onModeHitRef = useRef(onModeHit);
  onModeHitRef.current = onModeHit;
  onModeHoverRef.current = onModeHoverChange;
  const resetModeRef = useRef(null);
  // Mode unchecked (no candidates left): drop the hover and its pointer cursor now.
  useEffect(() => {
    if (!modeRows || !modeRows.length) resetModeRef.current && resetModeRef.current();
  }, [modeRows]);

  useEffect(() => {
    const container = map.getContainer();
    if (!container) return undefined;
    let down = null;
    let overShip = false;
    let hoveredKey = null;

    const rowKey = (row) => (row ? (row.key || row.shipment?.container_number || null) : null);

    // Same heading the ShipMarker icon is rotated by; cached per row because
    // it parses route geometry and hitAt runs on every mousemove.
    const headingCache = new Map();
    const headingOf = (row, pos) => {
      if (!headingCache.has(row)) {
        let h = 0;
        try { h = shipmentHeading(row.shipment, pos); } catch { h = 0; }
        headingCache.set(row, Number.isFinite(h) ? h : 0);
      }
      return headingCache.get(row);
    };

    const setHovered = (row) => {
      const key = rowKey(row);
      if (key === hoveredKey) return;
      hoveredKey = key;
      onHoverRef.current && onHoverRef.current(row || null);
    };

    // One pick for hover AND click: ships (visual arrow centre) and air/truck
    // markers (marker centre) compete; the nearest centre inside its own radius wins.
    const pickAt = (ev) => {
      if (ev.target && ev.target.closest && ev.target.closest('.leaflet-control, .leaflet-popup, .hazard-icon, input, textarea, select, button, a')) {
        return null;
      }
      const rect = container.getBoundingClientRect();
      const pt = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
      const ships = [];
      (rows || []).forEach((row) => {
        const candidates = [];
        if (row?.position) candidates.push(row.position);
        else if (row?.pin) candidates.push(row.pin);
        candidates.forEach((pos) => {
          if (!pos || !Number.isFinite(pos[0]) || !Number.isFinite(pos[1])) return;
          const pixel = map.latLngToContainerPoint(pos);
          // Measure from the visible triangle, not the icon anchor: it sits
          // ARROW_CENTER_OFFSET_PX ahead of the anchor along the heading.
          const rad = (headingOf(row, pos) * Math.PI) / 180;
          ships.push({
            row,
            x: pixel.x + Math.sin(rad) * ARROW_CENTER_OFFSET_PX,
            y: pixel.y - Math.cos(rad) * ARROW_CENTER_OFFSET_PX,
          });
        });
      });
      const modes = [];
      (modeRowsRef.current || []).forEach((m) => {
        if (!m.position) return;
        const px = map.latLngToContainerPoint(m.position);
        modes.push({ key: m.key, selected: Boolean(m.selected), x: px.x, y: px.y });
      });
      return pickMapHit(pt, ships, modes);
    };
    const shipOf = (p) => (p && p.kind === 'ship' ? p.cand.row : null);

    const onDown = (ev) => { down = { x: ev.clientX, y: ev.clientY }; };

    let modeHoverKey = null;
    let overMode = false;
    let moving = false;
    const setModeHover = (key) => {
      if (key === modeHoverKey) return;
      modeHoverKey = key;
      onModeHoverRef.current && onModeHoverRef.current(key);
    };
    const onMove = (ev) => {
      const picked = pickAt(ev);
      const hit = shipOf(picked);
      setHovered(hit);
      // No plane/truck hover resolution while the map is panning or zooming.
      const modeHit = picked && picked.kind === 'mode' && !moving ? picked.cand : null;
      setModeHover(modeHit ? modeHit.key : null);
      overMode = Boolean(modeHit);
      const next = Boolean(hit) || Boolean(modeHit);
      if (next === overShip) return;
      overShip = next;
      // Click hand over a shipment; Leaflet grab elsewhere (pointer-events:none on icons).
      container.style.cursor = next ? 'pointer' : '';
    };

    const onLeave = (ev) => {
      // Capture-phase mouseleave also fires for every child (route path, icons);
      // only a leave of the map container itself should clear the hover.
      if (ev && ev.target !== container) return;
      overShip = false;
      container.style.cursor = '';
      setHovered(null);
      setModeHover(null);
    };
    // Clears only the plane/truck hover (and its cursor); ship hover is untouched.
    const clearModeHover = () => {
      setModeHover(null);
      if (overMode) { overMode = false; overShip = false; container.style.cursor = ''; }
    };
    resetModeRef.current = clearModeHover;
    const onMoveStart = () => { moving = true; clearModeHover(); };
    const onMoveEnd = () => { moving = false; clearModeHover(); };
    map.on('movestart zoomstart', onMoveStart);
    map.on('moveend zoomend', onMoveEnd);

    const handler = (ev) => {
      if (down && (Math.abs(ev.clientX - down.x) > 5 || Math.abs(ev.clientY - down.y) > 5)) return;
      const t = ev.target && ev.target.closest ? ev.target : null;
      // Hazard markers sit above the ships and own their clicks: no ship hit,
      // and no miss either, so the selected shipment stays open.
      if (t && t.closest('.hazard-icon')) return;
      // Air/truck popups (content + close button) own their clicks.
      if (t && t.closest('.mode-popup-wrap')) return;
      const picked = pickAt(ev);
      const hit = shipOf(picked);
      // Same winner as hover. A plane/truck winner (or its marker/route) is left
      // to the mode layer so the sea selection stays.
      if (!hit && picked && picked.kind === 'mode') {
        // Open the picker's winner explicitly (the native target may be an
        // overlapping, larger marker) and keep the native click from firing twice.
        if (onModeHitRef.current) {
          ev.stopPropagation();
          ev.preventDefault();
          onModeHitRef.current(picked.cand.key);
        }
        return;
      }
      if (!hit && t && t.closest('.mode-marker--air, .mode-marker--truck, .mode-route')) return;
      if (hit) {
        ev.stopPropagation();
        ev.preventDefault();
        onHit && onHitRef.current(hit);
        return;
      }
      onMiss && onMissRef.current();
    };
    container.addEventListener('mousedown', onDown, true);
    container.addEventListener('mousemove', onMove, true);
    container.addEventListener('mouseleave', onLeave, true);
    container.addEventListener('click', handler, true);
    return () => {
      container.style.cursor = '';
      if (hoveredKey) setHovered(null);
      setModeHover(null);
      resetModeRef.current = null;
      map.off('movestart zoomstart', onMoveStart);
      map.off('moveend zoomend', onMoveEnd);
      container.removeEventListener('mousedown', onDown, true);
      container.removeEventListener('mousemove', onMove, true);
      container.removeEventListener('mouseleave', onLeave, true);
      container.removeEventListener('click', handler, true);
    };
  }, [map, rows]);

  return null;
}

/**
 * Programmatic zoom-to-region handler. When the URL supplies `?country=…` (and
 * optionally `?region=…`) the map fits to the bounding box of the matching
 * regions. Free panning is preserved — we set no `maxBounds` and rely on the
 * default drag handlers. `invalidateSize` runs after the animation so layout
 * shifts (e.g. sidebar collapse) don't leave the map mis-centered.
 *
 * Falls back gracefully when no matches are found or coordinates are invalid.
 */
export function RegionZoomHandler({ regions, country, region }) {
  const map = useMap();

  useEffect(() => {
    if (!country) return;
    if (!Array.isArray(regions) || regions.length === 0) return;

    const matchCountry = country.toLowerCase();
    const matchRegion = region ? region.toLowerCase() : null;

    const matches = regions.filter((r) => {
      const rc = (r?.country || '').toLowerCase();
      if (rc !== matchCountry) return false;
      if (matchRegion) {
        const rr = (r?.region || '').toLowerCase();
        if (rr !== matchRegion) return false;
      }
      return r?.latitude != null && r?.longitude != null;
    });

    if (matches.length === 0) return;

    const points = matches.map((r) => [r.latitude, r.longitude]);
    try {
      const bounds = L.latLngBounds(points);
      if (!bounds.isValid()) return;
      map.flyToBounds(bounds, { padding: [80, 40], duration: 0.6 });
      // Run invalidateSize once flyToBounds finishes so layout shifts (e.g.
      // sidebar collapse on arrival) don't leave the map mis-centered. Hooking
      // `moveend` is more robust than a setTimeout coupled to `duration`.
      const onMoveEnd = () => map.invalidateSize({ animate: false });
      map.once('moveend', onMoveEnd);
      return () => {
        map.off('moveend', onMoveEnd);
      };
    } catch {
      // Leaflet not available or invalid bounds — silently no-op.
    }
  }, [map, regions, country, region]);

  return null;
}

/**
 * Keeps the map centered on `center` after mount. `MapContainer`'s `center`
 * prop only applies on initial mount — Leaflet ignores prop changes on an
 * already-mounted map — so orientation/resize-driven recentering (e.g. the
 * portrait vs. landscape center used by signage displays) needs an explicit
 * `setView` call. Keeps the current zoom level; only pans.
 *
 * Skips the recenter while an explicit location target (country/region zoom
 * or a focused alert) is active — otherwise an orientation change would wipe
 * out that deep-link/focus navigation by yanking the map back to `center`.
 */
export function MapCenterHandler({ center, hasLocationTarget = false }) {
  const map = useMap();
  const prevCenter = useRef(center);

  useEffect(() => {
    const same =
      prevCenter.current?.[0] === center?.[0]
      && prevCenter.current?.[1] === center?.[1];
    prevCenter.current = center;
    // Only recenter when `center` itself changes (portrait/landscape).
    // Do not setView when a location target clears (deselect / close card).
    if (hasLocationTarget) return;
    if (same) return;
    if (!Array.isArray(center) || center.length !== 2) return;
    map.setView(center, map.getZoom());
  }, [center, hasLocationTarget, map]);

  return null;
}

/**
 * Flies to a region and opens its marker popup when a Harvest Alerts card is clicked.
 * `focusedRegion` gets a fresh object reference per click (MapPage adds a `_seq`) so
 * re-clicking the same card re-triggers this effect. The `mounted` guard avoids calling
 * `openPopup` on a detached marker if the map unmounts mid-flight.
 */
export function FocusRegionHandler({ focusedRegion, markerRefs }) {
  const map = useMap();

  useEffect(() => {
    if (!focusedRegion || focusedRegion.latitude == null || focusedRegion.longitude == null) return;
    let mounted = true;
    const marker = markerRefs.current.get(regionMarkerId(focusedRegion));
    // The pin may be drawn on the storm's side of the date line; fly to where it actually is.
    const latlng = marker ? marker.getLatLng() : [focusedRegion.latitude, focusedRegion.longitude];
    map.flyTo(latlng, Math.max(map.getZoom(), 6), { duration: 0.6 });
    const openPopup = () => {
      if (!mounted) return;
      const m = markerRefs.current.get(regionMarkerId(focusedRegion));
      if (m) m.openPopup();
    };
    map.once('moveend', openPopup);
    return () => {
      mounted = false;
      map.off('moveend', openPopup);
    };
  }, [focusedRegion, map, markerRefs]);

  return null;
}

/** Fly to the selected vessel AIS pin so a delayed ship off-coast is not missed. */
export function FocusShipmentHandler({ shipment }) {
  const map = useMap();
  const lastId = useRef(null);

  useEffect(() => {
    const id = shipment?.container_number || null;
    if (!id) {
      lastId.current = null;
      return; // close / deselect: leave camera
    }
    if (lastId.current === id) return;
    lastId.current = id;
    const pin = shipmentPinLatLng(shipment);
    if (!pin) return;
    map.flyTo(pin, Math.max(map.getZoom(), 6), { duration: 0.6 });
  }, [map, shipment?.container_number]);

  return null;
}



